import { useCallback, useRef } from "react"

import type { ButtonBaseProps } from "@mui/material"
import { Box, ButtonBase, Stack, Typography } from "@mui/material"
import { alpha, styled } from "@mui/material/styles"
import AutoHeight from "embla-carousel-auto-height"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import { find, reject } from "lodash"

import type { FC, FCWC, ResponsiveImageType } from ".."
import { Button, Container, Image, ResponsiveImage, SvgIcon, TrustPilotWidget } from ".."

type Review = {
  title: string
  review: string
  reviewer_name: string
}

type ReviewsCTAProps = {
  image: ResponsiveImageType
  reviews: Review[]
  variation?: "trustpilot" | "glassdoor"
}

const ReviewsCTA: FC<ReviewsCTAProps> = ({ reviews, image, variation = "trustpilot" }) => {
  // Compose image variants without original default size image and with extra
  // medium breakpoint that clone from 3xl so we avoid pixelation in medium screens.
  const imageVariants = reject(image?.variants, { style: "default" }) || []
  const smallImageVariant = find(imageVariants, { style: "sm" })
  const xlImageVariant = find(imageVariants, { style: "3xl" })
  const defaultImageVariant = smallImageVariant || xlImageVariant
  const augmentedImageVariants = {
    variants: imageVariants,
  }

  if (xlImageVariant) {
    augmentedImageVariants.variants.push({ ...xlImageVariant, style: "md" })
  }

  if (defaultImageVariant) {
    augmentedImageVariants.variants.push({ ...defaultImageVariant, style: "default" })
  }

  const autoheight = useRef(AutoHeight())
  const autoplay = useRef(Autoplay({ delay: 8000 }))
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      containScroll: "trimSnaps",
      loop: true,
      duration: 40,
    },
    [autoheight.current, autoplay.current]
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <Container>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "100%", lg: "repeat(2, minmax(0, 1fr))" }}
        py={{ xs: 2, md: 10 }}
        gap={{ xs: 0, md: 5 }}
        minHeight={{ xs: "auto", md: 700 }}
      >
        <Stack
          width="100%"
          position="relative"
          height={{ xs: 366, md: 700 }}
          alignItems="center"
          justifyContent="center"
          bgcolor={(theme) => theme.palette["neutral.200"].main}
          borderRadius={1.5}
          p={5}
        >
          <WidgetImage image={augmentedImageVariants} />
          <Stack
            justifyContent="center"
            alignItems="center"
            height={{ xs: "auto", md: 376 }}
            maxHeight={{ xs: 326, md: 760 }}
            width={{ xs: "80%", md: 352 }}
            bgcolor={(theme) => theme.palette["neutral.100"].main}
            p={{ xs: 5, md: 0 }}
            py={{ xs: 10, md: 0 }}
            borderRadius={1.5}
            zIndex={10}
            sx={{
              textAlign: "center",
              ...(variation === "glassdoor" && {}),

              iframe: {
                width: { xs: "auto!important", md: "100%!important" },
              },
            }}
          >
            {variation === "trustpilot" && <TrustPilotWidget variant="quotes" />}
            {variation === "glassdoor" && <GlassdoorContent />}
          </Stack>
        </Stack>
        <ReviewsCTAContentWrapper>
          <Box>
            <Image
              src="/images/plane.svg"
              alt="Plane Illustration"
              sx={{ width: { xs: 110, md: 145 }, height: { xs: 38, md: 50 } }}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={10}>
            <Box ref={emblaRef} overflow="hidden">
              <Box
                display="grid"
                gridAutoFlow="column"
                rowGap={20}
                columnGap={3}
                gridAutoColumns={{ xs: "minmax(100%, auto)", md: "100%" }}
                sx={{
                  alignItems: "flex-start",
                  transition: (theme) => theme.transitions.create("height"),
                }}
              >
                {reviews?.map((quote, i) => (
                  <ReviewItem key={i} {...quote} />
                ))}
              </Box>
            </Box>
            <Box display="flex" gap={5}>
              {emblaApi?.canScrollPrev && (
                <CarouselBtn onClick={scrollPrev} aria-label="Previous button">
                  <SvgIcon
                    variant="arrow"
                    size={{ xs: 16, md: 20 }}
                    sx={{
                      fill: (theme) => theme.palette["neutral.700"].main,
                      background: (theme) => theme.palette["neutral.100"].main,
                      borderRadius: (theme) => theme.spacing(25),
                      transform: (theme) =>
                        theme.transitions.create("fill", {
                          duration: theme.transitions.duration.standard,
                        }),
                    }}
                  />
                </CarouselBtn>
              )}
              {emblaApi?.canScrollNext && (
                <CarouselBtn onClick={() => scrollNext()} aria-label="Next button">
                  <SvgIcon
                    variant="arrow"
                    size={{ xs: 16, md: 20 }}
                    sx={{
                      fill: (theme) => theme.palette["neutral.700"].main,
                      background: (theme) => theme.palette["neutral.100"].main,
                      rotate: "180deg",
                      transform: (theme) =>
                        theme.transitions.create("fill", {
                          duration: theme.transitions.duration.standard,
                        }),
                    }}
                  />
                </CarouselBtn>
              )}
            </Box>
          </Box>
        </ReviewsCTAContentWrapper>
      </Box>
    </Container>
  )
}

export default ReviewsCTA

/** styled components  */
const ReviewsCTAContentWrapper = styled("section")(
  ({ theme }) => `
  padding: ${theme.spacing(7, 15, 15)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${theme.breakpoints.down("lg")} {
    padding: ${theme.spacing(15, 10, 15, 5)};
    gap: ${theme.spacing(10)};
  }
`
)

/** A sub-component for the quote item */
const ReviewItem: FC<Review> = ({ title, reviewer_name, review }) => {
  return (
    <Box display="flex" flexDirection="column" gap={5}>
      <ReviewTitle title={title} />
      <ReviewDescription review={review} />
      <ReviewSubtitle reviewer_name={reviewer_name} />
    </Box>
  )
}

/** A sub-component for the quote title */
type ReviewTitleType = Pick<Review, "title">
const ReviewTitle: FC<ReviewTitleType> = ({ title }) => {
  return <Typography variant="heading.xl" component="h2">&ldquo;{title}&rdquo;</Typography>
}

/** A sub-component for the quote reviewer_name */
type ReviewSubtitleType = Pick<Review, "reviewer_name">
const ReviewSubtitle: FC<ReviewSubtitleType> = ({ reviewer_name }) => {
  return <Typography variant="link.sm">{reviewer_name}</Typography>
}

/** A sub-component for the quote description */
type ReviewDescriptionType = Pick<Review, "review">
const ReviewDescription: FC<ReviewDescriptionType> = ({ review }) => {
  return <Typography variant="body.md" dangerouslySetInnerHTML={{ __html: review }} />
}

const ButtonWithLabel = (props: ButtonBaseProps) => (
  <ButtonBase aria-label="Carousel button" {...props} />
)
ButtonWithLabel.muiName = "ButtonWithLabel"

/** A sub-component for the Carousel button */
const CarouselBtn: FCWC<ButtonBaseProps> = ({ children, onClick }) => {
  return (
    <Box
      component={ButtonWithLabel}
      p={5}
      sx={(theme) => ({
        "width": { xs: 52, md: 62 },
        "height": { xs: 52, md: 62 },
        "pointerEvents": "all",
        "background": theme.palette["neutral.100"].main,
        "borderRadius": theme.spacing(25),
        "border": `1px solid ${theme.palette["neutral.200"].main}`,
        "transition": theme.transitions.create("box-shadow"),

        "&:hover": {
          boxShadow: `2px 2px 8px 0px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
        },
      })}
      onClick={onClick}
    >
      {children}
    </Box>
  )
}

/** A sub-component for the background image for the trustpilot widget */
const WidgetImage: FC<{ image: ResponsiveImageType }> = ({ image }) => (
  <>
    <ResponsiveImage
      image={image}
      position="absolute"
      width="100%"
      height="100%"
      top={0}
      left={0}
      bottom={0}
      right={0}
      sx={{ objectFit: "cover" }}
      zIndex={2}
      borderRadius={1.5}
    />
    <Box
      position="absolute"
      width="100%"
      height="100%"
      borderRadius={1.5}
      sx={{
        background: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette["neutral.900"].main, 0)} 0%, ${alpha(
            theme.palette["neutral.900"].main,
            0.4
          )} 100%)`,
        pointerEvents: "none",
      }}
      zIndex={2}
    />
  </>
)

const GlassdoorContent = () => (
  <Box>
    <Stack
      alignItems="center"
      justifyContent="center"
      direction="row"
      columnGap={2}
      mb={{ xs: 3, md: 7 }}
    >
      <Typography variant="body.md" fontWeight={500}>
        4.5
      </Typography>
      <Image
        src="/images/glassdoor-rating.svg"
        alt="Glassdoor Logo"
        sx={{ width: 94, height: 18 }}
      />
    </Stack>
    <Image src="/images/glassdoor-logo.svg" alt="Glassdoor Logo" sx={{ width: 242, height: 48 }} />
    <Box mt={{ xs: 8, md: 7 }}>
      <Button
        variant="secondary"
        href="https://www.glassdoor.ca/Overview/Working-at-Goway-Travel-EI_IE658334.11,23.htm"
      >
        Goway Profile
      </Button>
    </Box>
  </Box>
)
